import { supabase } from '../lib/supabase'
import { apiRequest } from './api'

// ── Users / Staff ─────────────────────────────────────────────────────────────

const USER_COLS = 'id, name, phone, email, profile_photo_url, role, status, department, is_active, created_at'

export const getUsers = async () => {
    const { data, error } = await supabase
        .from('users')
        .select(USER_COLS)
        .order('name')
    if (error) throw error
    return data
}

export const getUserById = async (id) => {
    const { data, error } = await supabase
        .from('users').select(USER_COLS).eq('id', id).single()
    if (error) throw error
    return data
}

export const updateUser = async (id, updates) => {
    return apiRequest('PUT', `/staff/users/${id}`, updates)
}

// ── Tasks ─────────────────────────────────────────────────────────────────────

export const getTasks = async (filters = {}) => {
    let q = supabase.from('tasks').select('*').order('created_at', { ascending: false })
    if (filters.status) q = q.eq('status', filters.status)
    if (filters.assigned) q = q.eq('assigned_to', filters.assigned)
    if (filters.category) q = q.eq('task_category', filters.category)
    if (filters.priority) q = q.eq('priority', filters.priority)
    const { data, error } = await q
    if (error) throw error
    return data
}

export const createTask = async (task) => {
    return apiRequest('POST', '/staff/tasks', task)
}

export const createTasks = async (tasks) => {
    return apiRequest('POST', '/staff/tasks', { batch: tasks })
}

export const updateTask = async (id, updates) => {
    return apiRequest('PUT', `/staff/tasks/${id}`, updates)
}

export const deleteTask = async (id) => {
    return apiRequest('DELETE', `/staff/tasks/${id}`)
}

// ── Customers ─────────────────────────────────────────────────────────────────

export const getCustomers = async (search = '', type = '') => {
    let q = supabase.from('customers').select('*').order('created_at', { ascending: false })
    if (search) q = q.ilike('customer_name', `%${search}%`)
    if (type) q = q.eq('customer_type', type)
    const { data, error } = await q
    if (error) throw error
    return data
}

export const createCustomer = async (customer) => {
    return apiRequest('POST', '/staff/customers', customer)
}

export const updateCustomer = async (id, updates) => {
    return apiRequest('PUT', `/staff/customers/${id}`, updates)
}

export const deleteCustomer = async (id) => {
    return apiRequest('DELETE', `/staff/customers/${id}`)
}

// ── EV Inventory ──────────────────────────────────────────────────────────────

export const getInventory = async (search = '') => {
    let q = supabase.from('ev_inventory').select('*').order('name')
    if (search) q = q.ilike('name', `%${search}%`)
    const { data, error } = await q
    if (error) throw error
    return data
}

export const createInventoryItem = async (item) => {
    return apiRequest('POST', '/staff/ev_inventory', item)
}

export const updateInventoryItem = async (id, updates) => {
    return apiRequest('PUT', `/staff/ev_inventory/${id}`, updates)
}

export const deleteInventoryItem = async (id) => {
    return apiRequest('DELETE', `/staff/ev_inventory/${id}`)
}

// ── Service Parts (AbhaCare inventory) ────────────────────────────────────────

export const getParts = async (search = '') => {
    let q = supabase.from('parts').select('*').order('name')
    if (search) q = q.ilike('name', `%${search}%`)
    const { data, error } = await q
    if (error) throw error
    return data
}

export const createPart = async (part) => {
    return apiRequest('POST', '/staff/parts', part)
}

export const updatePart = async (id, updates) => {
    return apiRequest('PUT', `/staff/parts/${id}`, updates)
}

export const deletePart = async (id) => {
    return apiRequest('DELETE', `/staff/parts/${id}`)
}

// ── Bills (AbhaFlow billing) ──────────────────────────────────────────────────

export const getBills = async () => {
    const { data, error } = await supabase
        .from('bills').select('*').order('created_at', { ascending: false })
    if (error) throw error
    return data
}

export const createBill = async (bill) => {
    return apiRequest('POST', '/staff/bills', bill)
}

export const updateBill = async (id, updates) => {
    return apiRequest('PUT', `/staff/bills/${id}`, updates)
}

// ── BSNL Connections ──────────────────────────────────────────────────────────

export const getBsnlConnections = async () => {
    const { data, error } = await supabase
        .from('bsnl_connections').select('*').order('created_at', { ascending: false })
    if (error) throw error
    return data
}

export const createBsnlConnection = async (conn) => {
    return apiRequest('POST', '/staff/bsnl_connections', conn)
}

export const updateBsnlConnection = async (id, updates) => {
    return apiRequest('PUT', `/staff/bsnl_connections/${id}`, updates)
}

export const deleteBsnlConnection = async (id) => {
    return apiRequest('DELETE', `/staff/bsnl_connections/${id}`)
}

// ── BSNL Restorations ─────────────────────────────────────────────────────────

export const getBsnlRestorations = async () => {
    const { data, error } = await supabase
        .from('bsnl_restorations').select('*').order('created_at', { ascending: false })
    if (error) throw error
    return data
}

export const createBsnlRestoration = async (rest) => {
    return apiRequest('POST', '/staff/bsnl_restorations', rest)
}

// ── Call Logs ─────────────────────────────────────────────────────────────────

export const getCallLogs = async (filters = {}) => {
    let q = supabase.from('call_logs')
        .select('*')
        .order('created_at', { ascending: false })
    if (filters.staff_id) q = q.eq('logged_by_id', filters.staff_id)
    if (filters.needs_follow_up) q = q.eq('needs_follow_up', true)
    if (filters.priority) q = q.eq('priority', filters.priority)
    if (filters.call_status) q = q.eq('call_status', filters.call_status)
    const { data, error } = await q
    if (error) throw error
    return data
}

export const createCallLog = async (log) => {
    return apiRequest('POST', '/staff/call_logs', log)
}

export const updateCallLog = async (id, updates) => {
    return apiRequest('PUT', `/staff/call_logs/${id}`, updates)
}

export const deleteCallLog = async (id) => {
    return apiRequest('DELETE', `/staff/call_logs/${id}`)
}

// ── Attendance / Time Sessions ────────────────────────────────────────────────

export const getTimeSessions = async (filters = {}) => {
    let q = supabase.from('staff_time_sessions')
        .select('*')
        .order('session_date', { ascending: false })
    if (filters.staff_id) q = q.eq('staff_id', filters.staff_id)
    const { data, error } = await q
    if (error) throw error
    return data
}

// ── Quotations ────────────────────────────────────────────────────────────────

export const getQuotations = async () => {
    const { data, error } = await supabase
        .from('quotations').select('*').order('created_at', { ascending: false })
    if (error) throw error
    return data
}

export const createQuotation = async (q) => {
    return apiRequest('POST', '/staff/quotations', q)
}

export const updateQuotation = async (id, updates) => {
    return apiRequest('PUT', `/staff/quotations/${id}`, updates)
}

// ── Tickets ───────────────────────────────────────────────────────────────────

export const getTickets = async () => {
    const { data, error } = await supabase
        .from('tickets').select('*').order('created_at', { ascending: false })
    if (error) throw error
    return data
}

export const createTicket = async (t) => {
    return apiRequest('POST', '/staff/tickets', t)
}

export const updateTicket = async (id, updates) => {
    return apiRequest('PUT', `/staff/tickets/${id}`, updates)
}

// ── Bookings (staff CRUD) ─────────────────────────────────────────────────────

export const getBookings = async () => {
    const { data, error } = await supabase
        .from('bookings').select('*').order('created_at', { ascending: false })
    if (error) throw error
    return data
}

export const createBooking = async (data) => {
    return apiRequest('POST', '/staff/bookings', data)
}

export const updateBooking = async (id, updates) => {
    return apiRequest('PUT', `/staff/bookings/${id}`, updates)
}

// ── Documents (aggregated image gallery from all tables) ─────────────────────

export const getDocuments = async () => {
    const tables = [
        {
            table: 'bookings',
            cols: 'aadhar_photo_url,license_photo_url,jio_tag_photo_url,brand,customer_name,logged_by,created_at',
            map: (row) => {
                const brand = row.brand || 'Walk-in'
                return [
                    row.aadhar_photo_url && { url: row.aadhar_photo_url, source: `Booking • ${brand}`, label: 'Aadhar Photo', uploaded_by: row.logged_by, created_at: row.created_at },
                    row.license_photo_url && { url: row.license_photo_url, source: `Booking • ${brand}`, label: 'License Photo', uploaded_by: row.logged_by, created_at: row.created_at },
                    row.jio_tag_photo_url && { url: row.jio_tag_photo_url, source: `Booking • ${brand}`, label: 'Jio Tag Photo', uploaded_by: row.logged_by, created_at: row.created_at },
                ].filter(Boolean)
            },
        },
        {
            table: 'call_logs',
            cols: 'caller_name,logged_by,created_at,mahindra_aadhar_photo_url,mahindra_license_photo_url,mahindra_jio_tag_photo_url,ola_aadhar_photo_url,ola_license_photo_url,ola_jio_tag_photo_url,ather_aadhar_photo_url,ather_license_photo_url,ather_jio_tag_photo_url,ampere_aadhar_photo_url,ampere_license_photo_url,ampere_jio_tag_photo_url',
            map: (row) => [
                row.mahindra_aadhar_photo_url && { url: row.mahindra_aadhar_photo_url, source: 'Booking • Mahindra', label: 'Aadhar Photo', uploaded_by: row.logged_by, created_at: row.created_at },
                row.mahindra_license_photo_url && { url: row.mahindra_license_photo_url, source: 'Booking • Mahindra', label: 'License Photo', uploaded_by: row.logged_by, created_at: row.created_at },
                row.mahindra_jio_tag_photo_url && { url: row.mahindra_jio_tag_photo_url, source: 'Booking • Mahindra', label: 'Jio Tag', uploaded_by: row.logged_by, created_at: row.created_at },
                row.ola_aadhar_photo_url && { url: row.ola_aadhar_photo_url, source: 'Booking • OLA', label: 'Aadhar Photo', uploaded_by: row.logged_by, created_at: row.created_at },
                row.ola_license_photo_url && { url: row.ola_license_photo_url, source: 'Booking • OLA', label: 'License Photo', uploaded_by: row.logged_by, created_at: row.created_at },
                row.ola_jio_tag_photo_url && { url: row.ola_jio_tag_photo_url, source: 'Booking • OLA', label: 'Jio Tag', uploaded_by: row.logged_by, created_at: row.created_at },
                row.ather_aadhar_photo_url && { url: row.ather_aadhar_photo_url, source: 'Booking • Ather', label: 'Aadhar Photo', uploaded_by: row.logged_by, created_at: row.created_at },
                row.ather_license_photo_url && { url: row.ather_license_photo_url, source: 'Booking • Ather', label: 'License Photo', uploaded_by: row.logged_by, created_at: row.created_at },
                row.ather_jio_tag_photo_url && { url: row.ather_jio_tag_photo_url, source: 'Booking • Ather', label: 'Jio Tag', uploaded_by: row.logged_by, created_at: row.created_at },
                row.ampere_aadhar_photo_url && { url: row.ampere_aadhar_photo_url, source: 'Booking • Ampere', label: 'Aadhar Photo', uploaded_by: row.logged_by, created_at: row.created_at },
                row.ampere_license_photo_url && { url: row.ampere_license_photo_url, source: 'Booking • Ampere', label: 'License Photo', uploaded_by: row.logged_by, created_at: row.created_at },
                row.ampere_jio_tag_photo_url && { url: row.ampere_jio_tag_photo_url, source: 'Booking • Ampere', label: 'Jio Tag', uploaded_by: row.logged_by, created_at: row.created_at },
            ].filter(Boolean),
        },
        {
            table: 'bsnl_connections',
            cols: 'aadhar_photo_side_a,aadhar_photo_side_b,passport_live_photo,router_label_photo,name,lead_generated_by,created_at',
            map: (row) => [
                row.aadhar_photo_side_a && { url: row.aadhar_photo_side_a, source: `BSNL Connection • ${row.name}`, label: 'Aadhar Side A', uploaded_by: row.lead_generated_by, created_at: row.created_at },
                row.aadhar_photo_side_b && { url: row.aadhar_photo_side_b, source: `BSNL Connection • ${row.name}`, label: 'Aadhar Side B', uploaded_by: row.lead_generated_by, created_at: row.created_at },
                row.passport_live_photo && { url: row.passport_live_photo, source: `BSNL Connection • ${row.name}`, label: 'Passport Photo', uploaded_by: row.lead_generated_by, created_at: row.created_at },
                row.router_label_photo && { url: row.router_label_photo, source: `BSNL Connection • ${row.name}`, label: 'Router Label', uploaded_by: row.lead_generated_by, created_at: row.created_at },
            ].filter(Boolean),
        },
        {
            table: 'bsnl_restorations',
            cols: 'damaged_item_photo_url,restored_item_photo_url,name,created_by,created_at',
            map: (row) => [
                row.damaged_item_photo_url && { url: row.damaged_item_photo_url, source: `BSNL Restoration • ${row.name}`, label: 'Damaged Item', uploaded_by: row.created_by, created_at: row.created_at },
                row.restored_item_photo_url && { url: row.restored_item_photo_url, source: `BSNL Restoration • ${row.name}`, label: 'Restored Item', uploaded_by: row.created_by, created_at: row.created_at },
            ].filter(Boolean),
        },
        {
            table: 'customers',
            cols: 'photo_url,customer_name,created_by,created_at',
            map: (row) => [
                row.photo_url && { url: row.photo_url, source: `Customer • ${row.customer_name}`, label: 'Customer Photo', uploaded_by: row.created_by || 'System', created_at: row.created_at },
            ].filter(Boolean),
        },
        {
            table: 'loan_entries',
            cols: 'customer_photo_url,bill_image_url,customer_name,created_by_name,created_at',
            map: (row) => [
                row.customer_photo_url && { url: row.customer_photo_url, source: `Loan • ${row.customer_name}`, label: 'Customer Photo', uploaded_by: row.created_by_name, created_at: row.created_at },
                row.bill_image_url && { url: row.bill_image_url, source: `Loan • ${row.customer_name}`, label: 'Bill Image', uploaded_by: row.created_by_name, created_at: row.created_at },
            ].filter(Boolean),
        },
        {
            table: 'loan_payments',
            cols: 'payment_photo_url,recorded_by_name,created_at',
            map: (row) => [
                row.payment_photo_url && { url: row.payment_photo_url, source: 'Loan Payment', label: 'Payment Proof', uploaded_by: row.recorded_by_name, created_at: row.created_at },
            ].filter(Boolean),
        },
        {
            table: 'dealer_deliveries',
            cols: 'delivery_proof_url,logged_by,created_at',
            map: (row) => [
                row.delivery_proof_url && { url: row.delivery_proof_url, source: 'Dealer Delivery', label: 'Delivery Proof', uploaded_by: row.logged_by, created_at: row.created_at },
            ].filter(Boolean),
        },
        {
            table: 'customer_deliveries',
            cols: 'vehicle_photo_url,delivery_proof_url,customer_name,logged_by,created_at',
            map: (row) => [
                row.vehicle_photo_url && { url: row.vehicle_photo_url, source: `Customer Delivery • ${row.customer_name}`, label: 'Vehicle Photo', uploaded_by: row.logged_by, created_at: row.created_at },
                row.delivery_proof_url && { url: row.delivery_proof_url, source: `Customer Delivery • ${row.customer_name}`, label: 'Delivery Proof', uploaded_by: row.logged_by, created_at: row.created_at },
            ].filter(Boolean),
        },
        {
            table: 'tasks',
            cols: 'attachment_url,title,created_by_name,created_at',
            map: (row) => [
                row.attachment_url && { url: row.attachment_url, source: `Task • ${row.title}`, label: 'Attachment', uploaded_by: row.created_by_name, created_at: row.created_at },
            ].filter(Boolean),
        },
        {
            table: 'ev_models',
            cols: 'photo_url,model_name,created_at',
            map: (row) => [
                row.photo_url && { url: row.photo_url, source: `Catalog • ${row.model_name}`, label: 'Model Photo', uploaded_by: 'System', created_at: row.created_at },
            ].filter(Boolean),
        },
        {
            table: 'nearby_stores',
            cols: 'store_photo_url,store_name,added_by,created_at',
            map: (row) => [
                row.store_photo_url && { url: row.store_photo_url, source: `Nearby Store • ${row.store_name}`, label: 'Store Photo', uploaded_by: row.added_by, created_at: row.created_at },
            ].filter(Boolean),
        },
    ]

    const isUrl = (s) => s && typeof s === 'string' && (s.startsWith('http://') || s.startsWith('https://'))

    const results = await Promise.allSettled(
        tables.map(async ({ table, cols, map }) => {
            const { data, error } = await supabase
                .from(table)
                .select(cols)
                .order('created_at', { ascending: false })
            if (error) return []
            return (data || []).flatMap(row => map(row).filter(d => isUrl(d.url)))
        })
    )

    const all = results.flatMap(r => r.status === 'fulfilled' ? r.value : [])
    return all.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
}

// ── Notifications ─────────────────────────────────────────────────────────────

export const sendNotification = async (payload) => {
    const { data, error } = await supabase.functions.invoke('send-notification', { body: payload })
    if (error) throw error
    return data
}

// ── Dashboard stats ───────────────────────────────────────────────────────────

export const getDashboardStats = async () => {
    const [tasks, customers, inventory, bsnl, staff] = await Promise.all([
        supabase.from('tasks').select('id, status'),
        supabase.from('customers').select('id', { count: 'exact', head: true }),
        supabase.from('ev_inventory').select('id, quantity, price'),
        supabase.from('bsnl_connections').select('id', { count: 'exact', head: true }),
        supabase.from('users').select('id, is_active, status'),
    ])

    const taskData = tasks.data || []
    const inventoryData = inventory.data || []
    const staffData = staff.data || []

    return {
        totalTasks: taskData.length,
        pendingTasks: taskData.filter(t => t.status === 'PENDING').length,
        inProgressTasks: taskData.filter(t => t.status === 'IN_PROGRESS').length,
        completedTasks: taskData.filter(t => t.status === 'COMPLETED').length,
        totalCustomers: customers.count ?? 0,
        totalBsnl: bsnl.count ?? 0,
        activeStaff: staffData.filter(s => s.status === 'AVAILABLE').length,
        totalStaff: staffData.filter(s => s.is_active).length,
        inventoryItems: inventoryData.length,
        inventoryValue: inventoryData.reduce((s, i) => s + (i.quantity * parseFloat(i.price || 0)), 0),
    }
}
